import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import '../../App.css';
import './MangaPage.css';
import PageShell from '../../components/layout/PageShell';
import PageHeader from '../../components/layout/PageHeader';
import MangaApiService from '../../services/MangaApiService';
import ApiService from '../../services/ApiService';
import MangaAccessNotice from './MangaAccessNotice';
import MangaStudioNav from './MangaStudioNav';
import { extractUploadMeta,
    fetchAllTeamMangas,
    getImageUploadErrorMessage,
    getMangaId,
    getMangaTitle,
    getRejectionReason,
    getTeamId,
    isDeletedStatus,
    isRejectedStatus,
    translateStatus,
    unwrapList,
    unwrapResponse,
    getMangaErrorMessage,
} from './mangaStudioUtils';

const getChapterId = (chapter) => chapter?.id || chapter?.chapterId || chapter?.chapter_id;
const getChapterNumber = (chapter) => chapter?.number ?? chapter?.chapterNumber ?? chapter?.index ?? chapter?.chapter_index;
const getChapterTitle = (chapter) => chapter?.title || chapter?.name || '';
const resolveVolumeValue = (value) => {
    if (value === null || value === undefined) {
        return value;
    }
    if (typeof value !== 'object') {
        return value;
    }
    return value.number
        ?? value.volumeNumber
        ?? value.volume_number
        ?? value.index
        ?? value.volume
        ?? value.name
        ?? value.id;
};
const getChapterVolume = (chapter) => resolveVolumeValue(
    chapter?.volume
    ?? chapter?.volumeNumber
    ?? chapter?.volume_number
    ?? chapter?.vol
    ?? chapter?.volume_index
    ?? chapter?.volumeIndex
    ?? chapter?.volumeId
    ?? chapter?.volume_id
    ?? chapter?.volumeNo
    ?? chapter?.volume_no
);
// Accepts "10", "10.5" or "10,5"; returns undefined for an empty field and NaN for garbage.
const CHAPTER_NUMBER_PATTERN = /^\d+([.,]\d{1,2})?$/;
const parseChapterNumberInput = (value) => {
    const trimmed = String(value ?? '').trim();
    if (!trimmed) {
        return undefined;
    }
    if (!CHAPTER_NUMBER_PATTERN.test(trimmed)) {
        return NaN;
    }
    return Number(trimmed.replace(',', '.'));
};
const normalizeChapterSortValue = (chapter) => {
    const raw = getChapterNumber(chapter);
    if (raw === null || raw === undefined) {
        return null;
    }
    if (typeof raw === 'number') {
        return Number.isFinite(raw) ? raw : null;
    }
    const trimmed = String(raw).trim();
    if (!trimmed) {
        return null;
    }
    const normalized = trimmed.replace(',', '.');
    const parsed = Number(normalized);
    if (Number.isFinite(parsed)) {
        return parsed;
    }
    return trimmed.toLowerCase();
};

const compareChaptersByNumber = (left, right) => {
    const leftValue = normalizeChapterSortValue(left);
    const rightValue = normalizeChapterSortValue(right);
    const leftIsNumber = typeof leftValue === 'number';
    const rightIsNumber = typeof rightValue === 'number';
    if (leftIsNumber && rightIsNumber) {
        return leftValue - rightValue;
    }
    if (leftIsNumber) {
        return -1;
    }
    if (rightIsNumber) {
        return 1;
    }
    if (leftValue && rightValue) {
        if (leftValue < rightValue) {
            return -1;
        }
        if (leftValue > rightValue) {
            return 1;
        }
    }
    const leftTitle = getChapterTitle(left).toLowerCase();
    const rightTitle = getChapterTitle(right).toLowerCase();
    if (leftTitle < rightTitle) {
        return -1;
    }
    if (leftTitle > rightTitle) {
        return 1;
    }
    return 0;
};

const normalizePages = (pages) => {
    if (!pages) {
        return [];
    }
    if (Array.isArray(pages)) {
        return pages;
    }
    if (Array.isArray(pages?.items)) {
        return pages.items;
    }
    if (Array.isArray(pages?.content)) {
        return pages.content;
    }
    if (Array.isArray(pages?.data)) {
        return pages.data;
    }
    return [];
};

const unwrapPagesResponse = (response) => {
    if (!response) {
        return [];
    }
    const payload = response?.data || response;
    if (Array.isArray(payload)) {
        return payload;
    }
    if (Array.isArray(payload?.data)) {
        return payload.data;
    }
    if (Array.isArray(payload?.content)) {
        return payload.content;
    }
    if (Array.isArray(payload?.items)) {
        return payload.items;
    }
    return normalizePages(payload);
};

const getPageNumber = (page, index) => {
    if (!page) {
        return index + 1;
    }
    const value = page.pageNumber || page.number || page.page || page.index;
    return value || index + 1;
};

const getPageFileId = (page) => page?.fileId || page?.id || page?.file_id;
const getPageBucket = (page, fallback) => (
    page?.storageBucket
    || page?.storage_bucket
    || page?.bucket
    || fallback
);

const isRoleActive = (value) => value === true || value === 'true' || value === 1 || value === '1';
const parseNumeric = (value) => {
    if (value === null || value === undefined || value === '') {
        return null;
    }
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : null;
};
const normalizePendingLimit = (payload) => {
    if (!payload || typeof payload !== 'object') {
        return null;
    }
    const pendingCount = parseNumeric(payload.pendingCount ?? payload.pending_count ?? payload.count);
    const pendingLimit = parseNumeric(payload.pendingLimit ?? payload.pending_limit ?? payload.limit);
    const rawBlocked = payload.blocked ?? payload.isBlocked ?? payload.is_blocked;
    const blocked = rawBlocked !== undefined && rawBlocked !== null
        ? isRoleActive(rawBlocked)
        : (pendingCount !== null && pendingLimit !== null ? pendingCount > pendingLimit : false);
    return {
        teamId: payload.teamId ?? payload.team_id ?? payload.id,
        pendingCount,
        pendingLimit,
        blocked,
    };
};
const formatPendingLimit = (info) => {
    if (!info) {
        return null;
    }
    const count = info.pendingCount;
    const limit = info.pendingLimit;
    if (count === null || count === undefined || limit === null || limit === undefined) {
        return null;
    }
    return `${count} из ${limit}`;
};

const CHAPTER_TITLE_LIMIT = 128;

const MangaStudioChaptersPage = () => {
    const [team, setTeam] = useState(null);
    const [teamLoading, setTeamLoading] = useState(false);
    const [teamError, setTeamError] = useState(null);

    const [mangaList, setMangaList] = useState([]);
    const [mangaLoading, setMangaLoading] = useState(false);
    const [mangaError, setMangaError] = useState(null);
    const [selectedMangaId, setSelectedMangaId] = useState('');

    const [chapterTitle, setChapterTitle] = useState('');
    const [chapterNumber, setChapterNumber] = useState('');
    const [chapterVolume, setChapterVolume] = useState('');
    const [uploadFiles, setUploadFiles] = useState([]);
    const [uploadedPages, setUploadedPages] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [chapterMessage, setChapterMessage] = useState(null);
    const [chapterError, setChapterError] = useState(null);
    const [chapters, setChapters] = useState([]);
    const [chaptersLoading, setChaptersLoading] = useState(false);
    const [chaptersError, setChaptersError] = useState(null);
    const [selectedChapterId, setSelectedChapterId] = useState('');
    const [composeNew, setComposeNew] = useState(false);
    const [searchParams] = useSearchParams();
    const presetMangaId = searchParams.get('manga');

    // Opened from the manga editor: start on that title.
    useEffect(() => {
        if (presetMangaId) setSelectedMangaId(String(presetMangaId));
    }, [presetMangaId]);
    const [editChapterTitle, setEditChapterTitle] = useState('');
    const [editChapterNumber, setEditChapterNumber] = useState('');
    const [editChapterVolume, setEditChapterVolume] = useState('');
    const [editChapterSaving, setEditChapterSaving] = useState(false);
    const [editPages, setEditPages] = useState([]);
    const [editPagesLoading, setEditPagesLoading] = useState(false);
    const [editPagesError, setEditPagesError] = useState(null);
    const [editUploadFiles, setEditUploadFiles] = useState([]);
    const [editUploading, setEditUploading] = useState(false);
    const [editSaving, setEditSaving] = useState(false);
    const [editMessage, setEditMessage] = useState(null);
    const [editError, setEditError] = useState(null);
    const [appendPagesMode, setAppendPagesMode] = useState(true);
    const [previewPageIndex, setPreviewPageIndex] = useState(0);
    const [previewPageUrl, setPreviewPageUrl] = useState(null);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [previewError, setPreviewError] = useState(null);
    const [uploadDragIndex, setUploadDragIndex] = useState(null);
    const [members, setMembers] = useState([]);
    const [membersError, setMembersError] = useState(null);
    const [selfProfile, setSelfProfile] = useState(null);
    const [pendingLimitInfo, setPendingLimitInfo] = useState(null);
    const [pendingLimitLoading, setPendingLimitLoading] = useState(false);
    const [pendingLimitError, setPendingLimitError] = useState(null);

    const isAllowed = MangaApiService.isAllowedDomain();
    const hasSession = !!localStorage.getItem('sessionKey');
    const teamId = getTeamId(team);
    const selfId = selfProfile?.data?.id || selfProfile?.id || localStorage.getItem('profileId');
    const isModerator = isRoleActive(selfProfile?.data?.mod ?? selfProfile?.mod ?? localStorage.getItem('mod'));
    const isEditor = isRoleActive(selfProfile?.data?.editor ?? selfProfile?.editor ?? localStorage.getItem('editor'));
    const isOwner = members.some((member) => {
        const memberId = member?.userId || member?.id || member?.uid;
        const role = String(member?.role || '').toUpperCase();
        return memberId && selfId && String(memberId) === String(selfId) && role === 'OWNER';
    });
    const canManageChapters = isOwner || isModerator || isEditor;
    const selectedChapter = useMemo(() => (
        chapters.find((chapter) => String(getChapterId(chapter)) === String(selectedChapterId)) || null
    ), [chapters, selectedChapterId]);
    const previewFallbackBucket = selectedChapter?.pagesStorageBucket || selectedChapter?.storageBucket || selectedChapter?.bucket || null;
    const selectedChapterStatus = selectedChapter?.status || selectedChapter?.state || selectedChapter?.moderationStatus;
    const selectedChapterRejectionReason = getRejectionReason(selectedChapter);
    const selectedChapterDeleted = isDeletedStatus(selectedChapterStatus)
        || Boolean(selectedChapter?.deletedAt || selectedChapter?.deleted_at || selectedChapter?.isDeleted || selectedChapter?.deleted);
    const pendingLimitSummary = useMemo(() => formatPendingLimit(pendingLimitInfo), [pendingLimitInfo]);
    const isPendingLimitBlocked = pendingLimitInfo?.blocked === true;
    const isChapterMetaDisabled = !hasSession
        || !selectedChapterId
        || !canManageChapters
        || editChapterSaving
        || selectedChapterDeleted
        || isPendingLimitBlocked;

    useEffect(() => {
        if (!selectedChapter) {
            setEditChapterTitle('');
            setEditChapterNumber('');
            setEditChapterVolume('');
            return;
        }
        const number = getChapterNumber(selectedChapter);
        const volume = getChapterVolume(selectedChapter);
        const title = getChapterTitle(selectedChapter);
        setEditChapterTitle(title || '');
        setEditChapterNumber(number === null || number === undefined ? '' : String(number));
        setEditChapterVolume(volume === null || volume === undefined ? '' : String(volume));
    }, [selectedChapter]);

    useEffect(() => {
        document.title = 'animix - Главы';
    }, []);

    const loadTeam = async () => {
        if (!hasSession || !isAllowed) {
            return;
        }
        setTeamLoading(true);
        setTeamError(null);
        try {
            const response = await MangaApiService.getMyTeam();
            const payload = unwrapResponse(response);
            setTeam(payload || null);
        } catch (err) {
            if (err?.response?.status === 404) {
                setTeam(null);
            } else {
                setTeamError(getMangaErrorMessage(err, 'Не удалось загрузить команду.'));
            }
        } finally {
            setTeamLoading(false);
        }
    };

    const loadTeamMangas = async (id) => {
        if (!id) {
            setMangaList([]);
            return;
        }
        setMangaLoading(true);
        setMangaError(null);
        try {
            setMangaList(await fetchAllTeamMangas(id));
        } catch (err) {
            setMangaError(getMangaErrorMessage(err, 'Не удалось загрузить мангу команды.'));
        } finally {
            setMangaLoading(false);
        }
    };

    const loadPendingLimit = async (id) => {
        if (!id) {
            setPendingLimitInfo(null);
            return null;
        }
        setPendingLimitLoading(true);
        setPendingLimitError(null);
        try {
            const response = await MangaApiService.getTeamPendingChapterLimit(id);
            const payload = unwrapResponse(response);
            const normalized = normalizePendingLimit(payload);
            setPendingLimitInfo(normalized);
            return normalized;
        } catch (err) {
            // The limit is a hint; a team without one (404) is not an error worth showing.
            if (err?.response?.status !== 404) {
                setPendingLimitError(getMangaErrorMessage(err, 'Не удалось загрузить лимит модерации.'));
            }
            setPendingLimitInfo(null);
            return null;
        } finally {
            setPendingLimitLoading(false);
        }
    };

    const loadChapters = async (mangaId) => {
        if (!mangaId) {
            setChapters([]);
            return;
        }
        setChaptersLoading(true);
        setChaptersError(null);
        try {
            const response = await MangaApiService.getChapters(mangaId, { includeDeleted: canManageChapters });
            const list = unwrapList(response);
            const sorted = [...list].sort(compareChaptersByNumber);
            setChapters(sorted);
        } catch (err) {
            setChaptersError(getMangaErrorMessage(err, 'Не удалось загрузить главы.'));
            setChapters([]);
        } finally {
            setChaptersLoading(false);
        }
    };

    const loadChapterPages = async (chapterId) => {
        if (!chapterId) {
            setEditPages([]);
            return;
        }
        setEditPagesLoading(true);
        setEditPagesError(null);
        try {
            const response = await MangaApiService.getChapterPages(chapterId);
            const list = unwrapPagesResponse(response);
            const normalized = normalizePages(list);
            const sorted = [...normalized].sort((left, right) => getPageNumber(left, 0) - getPageNumber(right, 0));
            setEditPages(sorted);
        } catch (err) {
            setEditPagesError(getMangaErrorMessage(err, 'Не удалось загрузить страницы главы.'));
            setEditPages([]);
        } finally {
            setEditPagesLoading(false);
        }
    };

    useEffect(() => {
        if (!isAllowed || !hasSession) {
            return;
        }
        loadTeam();
    }, [isAllowed, hasSession]);

    useEffect(() => {
        if (!hasSession) {
            setSelfProfile(null);
            return;
        }
        let isActive = true;
        const loadSelf = async () => {
            try {
                const response = await ApiService.getSelf();
                if (isActive) {
                    setSelfProfile(response?.data || response);
                }
            } catch (err) {
                if (isActive) {
                    setSelfProfile(null);
                }
            }
        };
        loadSelf();
        return () => {
            isActive = false;
        };
    }, [hasSession]);

    useEffect(() => {
        if (!teamId || !hasSession) {
            setMembers([]);
            return;
        }
        let isActive = true;
        const loadMembers = async () => {
            setMembersError(null);
            try {
                const response = await MangaApiService.getTeamMembers(teamId);
                const list = unwrapList(response);
                if (isActive) {
                    setMembers(list);
                }
            } catch (err) {
                if (isActive) {
                    setMembersError(getMangaErrorMessage(err, 'Не удалось загрузить участников.'));
                    setMembers([]);
                }
            }
        };
        loadMembers();
        return () => {
            isActive = false;
        };
    }, [teamId, hasSession]);

    useEffect(() => {
        if (!teamId) {
            setMangaList([]);
            setSelectedMangaId('');
            return;
        }
        loadTeamMangas(teamId);
    }, [teamId]);

    useEffect(() => {
        if (!teamId || !hasSession) {
            setPendingLimitInfo(null);
            setPendingLimitError(null);
            return;
        }
        loadPendingLimit(teamId);
    }, [teamId, hasSession]);

    useEffect(() => {
        if (!selectedMangaId && mangaList.length > 0) {
            const firstId = getMangaId(mangaList[0]);
            if (firstId !== undefined && firstId !== null) {
                setSelectedMangaId(String(firstId));
            }
        }
    }, [selectedMangaId, mangaList]);

    useEffect(() => {
        if (!selectedMangaId) {
            setChapters([]);
            setSelectedChapterId('');
            setEditPages([]);
            return;
        }
        setSelectedChapterId('');
        setEditPages([]);
        setEditMessage(null);
        setEditError(null);
        loadChapters(selectedMangaId);
    }, [selectedMangaId, canManageChapters]);

    useEffect(() => {
        if (chapters.length === 0) {
            if (selectedChapterId) {
                setSelectedChapterId('');
            }
            return;
        }
        if (selectedChapterId) {
            const exists = chapters.some((chapter) => (
                String(getChapterId(chapter)) === String(selectedChapterId)
            ));
            if (!exists) {
                setSelectedChapterId('');
            }
            return;
        }
        const firstId = getChapterId(chapters[0]);
        if (firstId !== undefined && firstId !== null) {
            setSelectedChapterId(String(firstId));
        }
    }, [selectedChapterId, chapters]);

    useEffect(() => {
        if (!selectedChapterId) {
            setEditPages([]);
            return;
        }
        loadChapterPages(selectedChapterId);
    }, [selectedChapterId]);

    useEffect(() => {
        setPreviewPageIndex(0);
        setPreviewPageUrl(null);
        setPreviewError(null);
    }, [selectedChapterId]);

    useEffect(() => {
        if (editPages.length === 0) {
            setPreviewPageIndex(0);
            setPreviewPageUrl(null);
            return;
        }
        if (previewPageIndex >= editPages.length) {
            setPreviewPageIndex(editPages.length - 1);
        }
    }, [editPages, previewPageIndex]);

    useEffect(() => {
        let isActive = true;
        const page = editPages[previewPageIndex];
        if (!page) {
            setPreviewPageUrl(null);
            setPreviewLoading(false);
            return () => {
                isActive = false;
            };
        }
        const bucket = getPageBucket(page, previewFallbackBucket);
        const fileId = getPageFileId(page);
        if (!bucket || !fileId) {
            setPreviewPageUrl(null);
            setPreviewLoading(false);
            return () => {
                isActive = false;
            };
        }
        setPreviewLoading(true);
        setPreviewError(null);
        const direct = MangaApiService.resolveFileUrl(page, bucket);
        if (direct) {
            if (isActive) {
                setPreviewPageUrl(direct);
                setPreviewLoading(false);
            }
            return () => {
                isActive = false;
            };
        }
        const resolveAsync = async () => {
            try {
                const resolved = await MangaApiService.resolveFileUrlAsync(page, bucket);
                if (isActive) {
                    setPreviewPageUrl(resolved);
                }
            } catch (err) {
                if (isActive) {
                    setPreviewPageUrl(null);
                    setPreviewError('Не удалось загрузить страницу.');
                }
            } finally {
                if (isActive) {
                    setPreviewLoading(false);
                }
            }
        };
        resolveAsync();
        return () => {
            isActive = false;
        };
    }, [editPages, previewPageIndex, previewFallbackBucket]);

    const handleUploadPages = async () => {
        setChapterMessage(null);
        setChapterError(null);
        if (!hasSession) {
            setChapterError('Войдите, чтобы загрузить страницы.');
            return;
        }
        if (uploadFiles.length === 0) {
            setChapterError('Выберите страницы для загрузки.');
            return;
        }
        setUploading(true);
        try {
            const results = [];
            for (const file of uploadFiles) {
                const response = await MangaApiService.uploadFile(file);
                results.push(extractUploadMeta(response, file.name));
            }
            setUploadedPages((prev) => [...prev, ...results]);
            setUploadFiles([]);
            setChapterMessage('Страницы загружены. Создайте главу, чтобы привязать их.');
        } catch (err) {
            setChapterError(getImageUploadErrorMessage(err, 'Не удалось загрузить страницы.'));
        } finally {
            setUploading(false);
        }
    };

    const handleUploadDragOver = (event) => {
        event.preventDefault();
    };

    const handleUploadDrop = (index) => {
        if (uploadDragIndex === null || uploadDragIndex === index) {
            return;
        }
        setUploadedPages((prev) => {
            const next = [...prev];
            const [item] = next.splice(uploadDragIndex, 1);
            next.splice(index, 0, item);
            return next;
        });
        setUploadDragIndex(null);
    };

    const handleUploadEditPages = async () => {
        setEditMessage(null);
        setEditError(null);
        if (!hasSession) {
            setEditError('Войдите, чтобы загрузить страницы.');
            return;
        }
        if (!selectedChapterId) {
            setEditError('Выберите главу для редактирования.');
            return;
        }
        if (editUploadFiles.length === 0) {
            setEditError('Выберите страницы для загрузки.');
            return;
        }
        setEditUploading(true);
        try {
            const results = [];
            for (const file of editUploadFiles) {
                const response = await MangaApiService.uploadFile(file);
                results.push(extractUploadMeta(response, file.name));
            }
            if (appendPagesMode) {
                const maxPageNumber = editPages.reduce((max, page, index) => (
                    Math.max(max, getPageNumber(page, index))
                ), 0);
                const payloadPages = results.map((page, index) => {
                    const fileId = page.fileId || page.id;
                    if (!fileId) {
                        return null;
                    }
                    return {
                        pageNumber: maxPageNumber + index + 1,
                        fileId,
                        storageBucket: page.bucket,
                    };
                }).filter(Boolean);
                if (payloadPages.length === 0) {
                    setEditError('Не удалось подготовить страницы для добавления.');
                    return;
                }
                await MangaApiService.setChapterPages(selectedChapterId, payloadPages, { append: true });
                setEditMessage('Страницы добавлены.');
                setEditUploadFiles([]);
                await loadChapterPages(selectedChapterId);
                await loadChapters(selectedMangaId);
                return;
            }
            setEditPages((prev) => [
                ...prev,
                ...results.map((page) => ({
                    fileId: page.fileId || page.id,
                    storageBucket: page.bucket,
                    name: page.name,
                })),
            ]);
            setEditUploadFiles([]);
            setEditMessage('Страницы добавлены. Сохраните порядок.');
        } catch (err) {
            setEditError(getImageUploadErrorMessage(err, 'Не удалось загрузить страницы.'));
        } finally {
            setEditUploading(false);
        }
    };

    const moveEditPage = (fromIndex, toIndex) => {
        setEditPages((prev) => {
            if (toIndex < 0 || toIndex >= prev.length) {
                return prev;
            }
            const next = [...prev];
            const [item] = next.splice(fromIndex, 1);
            next.splice(toIndex, 0, item);
            return next;
        });
    };

    const removeEditPage = (index) => {
        setEditPages((prev) => prev.filter((_, idx) => idx !== index));
    };

    const handlePreviewPrev = () => {
        setPreviewPageIndex((prev) => Math.max(0, prev - 1));
    };

    const handlePreviewNext = () => {
        setPreviewPageIndex((prev) => Math.min(editPages.length - 1, prev + 1));
    };

    const handleSavePageOrder = async () => {
        setEditMessage(null);
        setEditError(null);
        if (!hasSession) {
            setEditError('Войдите, чтобы сохранить порядок.');
            return;
        }
        if (!selectedChapterId) {
            setEditError('Выберите главу для редактирования.');
            return;
        }
        const fallbackBucket = getPageBucket(editPages[0], null);
        const payloadPages = editPages.map((page, index) => {
            const fileId = getPageFileId(page);
            if (!fileId) {
                return null;
            }
            const bucket = getPageBucket(page, fallbackBucket);
            return {
                pageNumber: index + 1,
                fileId,
                storageBucket: bucket,
            };
        }).filter(Boolean);
        if (payloadPages.length === 0) {
            setEditError('Список страниц пуст.');
            return;
        }
        setEditSaving(true);
        try {
            await MangaApiService.setChapterPages(selectedChapterId, payloadPages);
            setEditMessage('Порядок страниц сохранён.');
            await loadChapterPages(selectedChapterId);
            await loadChapters(selectedMangaId);
        } catch (err) {
            setEditError(getMangaErrorMessage(err, 'Не удалось сохранить порядок страниц.'));
        } finally {
            setEditSaving(false);
        }
    };

    const handleUpdateChapterMeta = async () => {
        setEditMessage(null);
        setEditError(null);
        if (!hasSession) {
            setEditError('Войдите, чтобы обновить параметры главы.');
            return;
        }
        if (!selectedChapterId) {
            setEditError('Выберите главу для редактирования.');
            return;
        }
        if (!canManageChapters) {
            setEditError('Редактирование доступно только владельцу команды, редактору или модератору.');
            return;
        }
        if (isPendingLimitBlocked) {
            const limitNote = pendingLimitSummary ? ` (${pendingLimitSummary})` : '';
            setEditError(`Слишком много глав на модерации${limitNote}. Дождитесь проверки.`);
            return;
        }
        const titleTrimmed = editChapterTitle.trim();
        if (titleTrimmed.length > CHAPTER_TITLE_LIMIT) {
            setEditError(`Название главы не может быть длиннее ${CHAPTER_TITLE_LIMIT} символов.`);
            return;
        }
        const numberValue = parseChapterNumberInput(editChapterNumber);
        const volumeValue = parseChapterNumberInput(editChapterVolume);
        if (Number.isNaN(numberValue) || Number.isNaN(volumeValue)) {
            setEditError('Номер главы и тома — число, можно с точкой: 10 или 10.5.');
            return;
        }
        const payload = {
            title: titleTrimmed || null,
            number: numberValue ?? null,
            volume: volumeValue ?? null,
        };
        setEditChapterSaving(true);
        try {
            await MangaApiService.updateChapter(selectedChapterId, payload);
            setEditMessage('Параметры главы обновлены и отправлены на модерацию.');
            await loadChapters(selectedMangaId);
            await loadPendingLimit(teamId);
        } catch (err) {
            setEditError(getMangaErrorMessage(err, 'Не удалось обновить главу.'));
        } finally {
            setEditChapterSaving(false);
        }
    };

    const handleDeleteChapter = async () => {
        if (!selectedChapterId) {
            setEditError('Выберите главу для удаления.');
            return;
        }
        if (!canManageChapters) {
            setEditError('Удаление доступно только владельцу команды, редактору или модератору.');
            return;
        }
        const confirmDelete = window.confirm('Удалить выбранную главу? В течение 7 дней доступно восстановление.');
        if (!confirmDelete) {
            return;
        }
        setEditMessage(null);
        setEditError(null);
        try {
            await MangaApiService.deleteChapter(selectedChapterId);
            setEditMessage('Глава удалена. В течение 7 дней можно восстановить.');
            await loadChapters(selectedMangaId);
        } catch (err) {
            setEditError(getMangaErrorMessage(err, 'Не удалось удалить главу.'));
        }
    };

    const handleRestoreChapter = async () => {
        if (!selectedChapterId) {
            setEditError('Выберите главу для восстановления.');
            return;
        }
        if (!canManageChapters) {
            setEditError('Восстановление доступно только владельцу команды, редактору или модератору.');
            return;
        }
        setEditMessage(null);
        setEditError(null);
        try {
            await MangaApiService.restoreChapter(selectedChapterId);
            setEditMessage('Глава восстановлена.');
            await loadChapters(selectedMangaId);
        } catch (err) {
            setEditError(getMangaErrorMessage(err, 'Не удалось восстановить главу.'));
        }
    };

    const handleCreateChapter = async () => {
        setChapterMessage(null);
        setChapterError(null);
        if (!hasSession) {
            setChapterError('Войдите, чтобы создать главу.');
            return;
        }
        if (!teamId) {
            setChapterError('Сначала создайте команду.');
            return;
        }
        if (!selectedMangaId) {
            setChapterError('Выберите мангу для главы.');
            return;
        }
        if (isPendingLimitBlocked) {
            const limitNote = pendingLimitSummary ? ` (${pendingLimitSummary})` : '';
            setChapterError(`Слишком много глав на модерации${limitNote}. Дождитесь проверки.`);
            return;
        }
        const pagesFromUploads = uploadedPages.map((page, index) => {
            const fileId = page.fileId || page.id;
            if (!fileId) {
                return null;
            }
            return {
                pageNumber: index + 1,
                fileId,
                storageBucket: page.bucket,
            };
        }).filter(Boolean);

        if (pagesFromUploads.length === 0) {
            setChapterError('Загрузите страницы перед созданием главы.');
            return;
        }

        const titleTrimmed = chapterTitle.trim();
        if (titleTrimmed.length > CHAPTER_TITLE_LIMIT) {
            setChapterError(`Название главы не может быть длиннее ${CHAPTER_TITLE_LIMIT} символов.`);
            return;
        }
        const numberValue = parseChapterNumberInput(chapterNumber);
        const volumeValue = parseChapterNumberInput(chapterVolume);
        if (Number.isNaN(numberValue) || Number.isNaN(volumeValue)) {
            setChapterError('Номер главы и тома — число, можно с точкой: 10 или 10.5.');
            return;
        }
        const payload = {
            teamId,
            ...(titleTrimmed ? { title: titleTrimmed } : {}),
            ...(numberValue !== undefined ? { number: numberValue } : {}),
            ...(volumeValue !== undefined ? { volume: volumeValue } : {}),
        };

        try {
            const response = await MangaApiService.createChapter(selectedMangaId, payload);
            const payloadResponse = response?.data || response;
            const data = payloadResponse?.data || payloadResponse;
            const chapterId = data?.id || data?.chapterId || data?.chapter_id || payloadResponse?.id;
            if (!chapterId) {
                throw new Error('Не удалось получить идентификатор главы.');
            }
            await MangaApiService.setChapterPages(chapterId, pagesFromUploads);
            setChapterMessage('Глава создана, страницы привязаны и отправлены на модерацию.');
            setChapterTitle('');
            setChapterNumber('');
            setChapterVolume('');
            setUploadedPages([]);
            await loadChapters(selectedMangaId);
            await loadPendingLimit(teamId);
        } catch (err) {
            setChapterError(getMangaErrorMessage(err, 'Не удалось создать главу или привязать страницы.'));
        }
    };

    if (!isAllowed) {
        return (
            <PageShell>
                <MangaAccessNotice />
            </PageShell>
        );
    }

    const showChapterEditor = Boolean(selectedChapterId) && !composeNew;
    const selectedMangaTitle = mangaList.find((manga) => String(getMangaId(manga)) === String(selectedMangaId));

    return (
        <PageShell className="manga-studio-page">
            <PageHeader title="Главы" eyebrow="Студия манги" />
            <MangaStudioNav />

            {!hasSession ? <div className="manga-inline-warning">Войдите, чтобы загружать главы.</div> : null}
            {teamError ? <div className="manga-inline-warning">{teamError}</div> : null}
            {membersError ? <div className="manga-inline-warning">{membersError}</div> : null}
            {!teamId && !teamLoading && hasSession ? (
                <div className="manga-inline-warning">
                    Сначала создайте команду в разделе <Link className="manga-rules-link" to="/manga/studio/team">«Команда»</Link>.
                </div>
            ) : null}

            <div className="studio-layout">
                {/* Left: pick a title, then a chapter. */}
                <aside className="studio-layout__side">
                    <div className="manga-panel">
                        <label className="manga-field">
                            <span>Манга</span>
                            <select
                                value={selectedMangaId || ''}
                                onChange={(event) => { setSelectedMangaId(event.target.value); setComposeNew(false); }}
                                disabled={!hasSession || mangaList.length === 0}
                            >
                                <option value="">Выберите мангу</option>
                                {mangaList.map((manga) => {
                                    const id = getMangaId(manga);
                                    return <option key={id} value={id}>{getMangaTitle(manga)}</option>;
                                })}
                            </select>
                        </label>
                        {teamLoading || mangaLoading ? <div className="loader" /> : null}
                        {mangaError ? <div className="manga-inline-warning">{mangaError}</div> : null}

                        <div className="studio-card__head studio-card__head--inner">
                            <h3>Главы{chapters.length ? <small>{chapters.length}</small> : null}</h3>
                            <button
                                type="button"
                                className={`ui-btn ui-btn--sm${showChapterEditor ? ' ui-btn--secondary' : ''}`}
                                onClick={() => setComposeNew(true)}
                                disabled={!selectedMangaId}
                            >
                                Новая
                            </button>
                        </div>
                        {chaptersLoading ? <div className="loader" /> : null}
                        {chaptersError ? <div className="manga-inline-warning">{chaptersError}</div> : null}
                        <div className="manga-studio-list manga-studio-list--scroll">
                            {selectedMangaId ? (
                                chapters.length > 0 ? (
                                    chapters.map((chapter) => {
                                        const chapterId = getChapterId(chapter);
                                        const number = getChapterNumber(chapter);
                                        const title = getChapterTitle(chapter);
                                        const status = chapter?.status || chapter?.state || chapter?.moderationStatus;
                                        const views = chapter?.views ?? chapter?.viewCount ?? chapter?.viewsCount ?? chapter?.views_count;
                                        const isSelected = showChapterEditor && chapterId && String(chapterId) === String(selectedChapterId);
                                        const label = number !== null && number !== undefined && number !== '' ? `Глава ${number}` : 'Глава';
                                        const meta = [status ? translateStatus(status) : null, views !== null && views !== undefined ? `${views} просм.` : null].filter(Boolean).join(' · ');
                                        return (
                                            <button
                                                key={chapterId || label}
                                                className={`manga-studio-item ${isSelected ? 'selected' : ''}`}
                                                onClick={() => { setSelectedChapterId(String(chapterId)); setComposeNew(false); }}
                                            >
                                                <div className="manga-studio-item-title">{title ? `${label} · ${title}` : label}</div>
                                                {meta ? <div className="manga-studio-item-meta">{meta}</div> : null}
                                            </button>
                                        );
                                    })
                                ) : (
                                    <div className="manga-empty">Глав пока нет.</div>
                                )
                            ) : (
                                <div className="manga-empty">Выберите мангу, чтобы увидеть главы.</div>
                            )}
                        </div>
                    </div>
                </aside>

                {/* Right: new chapter, or the selected chapter's settings and pages. */}
                <div className="studio-layout__main">
                    {!showChapterEditor ? (
                        <div className="manga-panel">
                            <div className="studio-card__head">
                                <h2>Новая глава{selectedMangaTitle ? <small>{getMangaTitle(selectedMangaTitle)}</small> : null}</h2>
                                {selectedChapterId ? (
                                    <button type="button" className="button-sec" onClick={() => setComposeNew(false)}>К выбранной главе</button>
                                ) : null}
                            </div>
                            {chapterError ? <div className="manga-inline-warning">{chapterError}</div> : null}
                            {pendingLimitError ? <div className="manga-inline-warning">{pendingLimitError}</div> : null}
                            {pendingLimitLoading ? <div className="loader" /> : null}
                            {pendingLimitInfo && isPendingLimitBlocked ? (
                                <div className="manga-inline-warning">
                                    Слишком много глав на модерации{pendingLimitSummary ? ` (${pendingLimitSummary})` : ''}. Дождитесь проверки.
                                </div>
                            ) : null}
                            <div className="manga-field-row studio-fields-3">
                                <label className="manga-field studio-field--wide">
                                    <span>Название главы</span>
                                    <input
                                        type="text"
                                        placeholder="Например, «Начало пути»"
                                        value={chapterTitle}
                                        onChange={(event) => setChapterTitle(event.target.value)}
                                        maxLength={CHAPTER_TITLE_LIMIT}
                                        disabled={!hasSession}
                                    />
                                </label>
                                <label className="manga-field">
                                    <span>Номер</span>
                                    <input type="text" placeholder="10 или 10.5" inputMode="decimal" value={chapterNumber} onChange={(event) => setChapterNumber(event.target.value)} disabled={!hasSession} />
                                </label>
                                <label className="manga-field">
                                    <span>Том</span>
                                    <input type="text" placeholder="Если есть" inputMode="decimal" value={chapterVolume} onChange={(event) => setChapterVolume(event.target.value)} disabled={!hasSession} />
                                </label>
                            </div>
                            <div className="manga-field">
                                <span>Страницы</span>
                                <div className="manga-studio-toolbar">
                                    <label className="upload-label">
                                        {uploadFiles.length > 0 ? `Выбрано файлов: ${uploadFiles.length}` : 'Выбрать страницы'}
                                        <input type="file" accept="image/*" multiple onChange={(event) => setUploadFiles(Array.from(event.target.files || []))} disabled={!hasSession} />
                                    </label>
                                    <button type="button" className="button-sec" onClick={handleUploadPages} disabled={!hasSession || uploading || uploadFiles.length === 0}>
                                        {uploading ? 'Загружаем…' : 'Загрузить'}
                                    </button>
                                    <span className="manga-help">JPEG, PNG или WEBP, до 10 МБ на страницу. Порядок меняется перетаскиванием.</span>
                                </div>
                            </div>
                            {uploadedPages.length > 0 ? (
                                <div className="manga-upload-list">
                                    {uploadedPages.map((page, index) => (
                                        <div
                                            key={`${page.id || page.fileId}-${index}`}
                                            className={`manga-upload-item${uploadDragIndex === index ? ' dragging' : ''}`}
                                            draggable
                                            onDragStart={() => setUploadDragIndex(index)}
                                            onDragOver={handleUploadDragOver}
                                            onDrop={() => handleUploadDrop(index)}
                                            onDragEnd={() => setUploadDragIndex(null)}
                                        >
                                            <span className="manga-upload-index">{index + 1}</span>
                                            <span className="manga-upload-name">{page.name || `Страница ${index + 1}`}</span>
                                        </div>
                                    ))}
                                </div>
                            ) : null}
                            <div className="studio-card__footer">
                                <button type="button" className="manga-primary-button" onClick={handleCreateChapter} disabled={!hasSession || !selectedMangaId || isPendingLimitBlocked}>
                                    Создать главу
                                </button>
                                <span className="manga-help">
                                    {pendingLimitSummary && !isPendingLimitBlocked ? `На модерации: ${pendingLimitSummary}. ` : ''}
                                    Страницы привяжутся к главе автоматически.
                                </span>
                            </div>
                            {chapterMessage ? <p className="manga-inline-note" role="status">{chapterMessage}</p> : null}
                        </div>
                    ) : (
                        <div className="manga-panel">
                            <div className="studio-card__head">
                                <h2>
                                    {(() => {
                                        const number = getChapterNumber(selectedChapter);
                                        const title = getChapterTitle(selectedChapter);
                                        const label = number !== null && number !== undefined && number !== '' ? `Глава ${number}` : 'Глава';
                                        return title ? `${label} · ${title}` : label;
                                    })()}
                                    {selectedChapterStatus ? <span className="studio-status">{translateStatus(selectedChapterStatus)}</span> : null}
                                </h2>
                            </div>
                            {editError ? <div className="manga-inline-warning">{editError}</div> : null}
                            {editPagesError ? <div className="manga-inline-warning">{editPagesError}</div> : null}
                            {isRejectedStatus(selectedChapterStatus) ? (
                                <div className="manga-inline-warning">
                                    Глава отклонена модерацией.{selectedChapterRejectionReason ? ` Причина: ${selectedChapterRejectionReason}` : ''} Исправьте замечания и сохраните главу — она снова уйдёт на проверку.
                                </div>
                            ) : null}
                            {isPendingLimitBlocked ? (
                                <div className="manga-inline-warning">
                                    Слишком много глав на модерации{pendingLimitSummary ? ` (${pendingLimitSummary})` : ''}. Дождитесь проверки.
                                </div>
                            ) : null}
                            {selectedChapterDeleted ? (
                                <div className="manga-inline-warning">
                                    Глава удалена. В течение 7 дней её можно восстановить.{' '}
                                    <button type="button" className="manga-rules-link studio-link-button" onClick={handleRestoreChapter} disabled={!hasSession || !canManageChapters}>Восстановить</button>
                                </div>
                            ) : null}
                            {!canManageChapters ? (
                                <div className="manga-help">Редактирование доступно только владельцу команды, редактору или модератору.</div>
                            ) : null}

                            <div className="manga-field-row studio-fields-3 studio-fields--with-action">
                                <label className="manga-field studio-field--wide">
                                    <span>Название главы</span>
                                    <input type="text" value={editChapterTitle} onChange={(event) => setEditChapterTitle(event.target.value)} maxLength={CHAPTER_TITLE_LIMIT} disabled={isChapterMetaDisabled} />
                                </label>
                                <label className="manga-field">
                                    <span>Номер</span>
                                    <input type="text" placeholder="10 или 10.5" inputMode="decimal" value={editChapterNumber} onChange={(event) => setEditChapterNumber(event.target.value)} disabled={isChapterMetaDisabled} />
                                </label>
                                <label className="manga-field">
                                    <span>Том</span>
                                    <input type="text" placeholder="Если есть" inputMode="decimal" value={editChapterVolume} onChange={(event) => setEditChapterVolume(event.target.value)} disabled={isChapterMetaDisabled} />
                                </label>
                                <button className="button-sec" onClick={handleUpdateChapterMeta} disabled={isChapterMetaDisabled}>
                                    {editChapterSaving ? 'Сохранение...' : 'Сохранить'}
                                </button>
                            </div>

                            <div className="studio-card__head studio-card__head--inner">
                                <h3>Страницы{editPages.length ? <small>{editPages.length}</small> : null}</h3>
                                <span className="manga-help">Изменения порядка и параметров отправят главу на повторную модерацию.</span>
                            </div>
                            {editPagesLoading ? <div className="loader" /> : null}
                            <div className="studio-pages">
                                <div className="studio-pages__list">
                                    {editPages.length > 0 ? (
                                        <div className="manga-page-list">
                                            {editPages.map((page, index) => {
                                                const pageNumber = getPageNumber(page, index);
                                                const fileId = getPageFileId(page);
                                                const pageName = page?.name || page?.fileName || page?.originalName || (fileId ? `Файл ${fileId}` : `Страница ${pageNumber}`);
                                                return (
                                                    <div key={`${fileId || pageNumber}-${index}`} className={`manga-page-row${index === previewPageIndex ? ' is-current' : ''}`}>
                                                        <div className="manga-page-index">{pageNumber}</div>
                                                        <div className="manga-page-name">{pageName}</div>
                                                        <div className="manga-page-actions">
                                                            <button type="button" className="button-sec studio-icon-button" onClick={() => moveEditPage(index, index - 1)} disabled={index === 0} aria-label="Вверх">↑</button>
                                                            <button type="button" className="button-sec studio-icon-button" onClick={() => moveEditPage(index, index + 1)} disabled={index === editPages.length - 1} aria-label="Вниз">↓</button>
                                                            <button type="button" className="manga-danger studio-icon-button" onClick={() => removeEditPage(index)} aria-label="Удалить">×</button>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    ) : (
                                        <div className="manga-empty">Страницы не найдены.</div>
                                    )}
                                    <div className="manga-toggle">
                                        <input type="checkbox" id="append-pages-mode" checked={appendPagesMode} onChange={(event) => setAppendPagesMode(event.target.checked)} disabled={!hasSession} />
                                        <label className="label-switch" htmlFor="append-pages-mode" />
                                        <span className="label-switch-text">Добавлять без замены</span>
                                    </div>
                                    <div className="manga-studio-toolbar">
                                        <label className="upload-label">
                                            {editUploadFiles.length > 0 ? `Выбрано файлов: ${editUploadFiles.length}` : 'Добавить страницы'}
                                            <input type="file" accept="image/*" multiple onChange={(event) => setEditUploadFiles(Array.from(event.target.files || []))} disabled={!hasSession} />
                                        </label>
                                        <button type="button" className="button-sec" onClick={handleUploadEditPages} disabled={!hasSession || editUploading || editUploadFiles.length === 0}>
                                            {editUploading ? 'Загружаем…' : 'Загрузить'}
                                        </button>
                                    </div>
                                </div>
                                {editPages.length > 0 ? (
                                    <div className="studio-pages__preview">
                                        {previewError ? <div className="manga-inline-warning">{previewError}</div> : null}
                                        <div className="manga-reader manga-reader--compact">
                                            <div className="manga-reader__toolbar">
                                                <button className="button-sec" onClick={handlePreviewPrev} disabled={previewPageIndex === 0}>Назад</button>
                                                <span className="manga-reader__counter">
                                                    {getPageNumber(editPages[previewPageIndex], previewPageIndex)} / {editPages.length}
                                                </span>
                                                <button className="button-sec" onClick={handlePreviewNext} disabled={previewPageIndex >= editPages.length - 1}>Вперёд</button>
                                            </div>
                                            <div className="manga-reader__page">
                                                {previewLoading ? (
                                                    <div className="loader" />
                                                ) : previewPageUrl ? (
                                                    <img src={previewPageUrl} alt={`Страница ${getPageNumber(editPages[previewPageIndex], previewPageIndex)}`} />
                                                ) : (
                                                    <div className="manga-empty">Страница недоступна.</div>
                                                )}
                                                <button type="button" className="manga-reader__nav manga-reader__nav--left" onClick={handlePreviewPrev} disabled={previewPageIndex === 0} aria-label="Предыдущая страница" />
                                                <button type="button" className="manga-reader__nav manga-reader__nav--right" onClick={handlePreviewNext} disabled={previewPageIndex >= editPages.length - 1} aria-label="Следующая страница" />
                                            </div>
                                        </div>
                                    </div>
                                ) : null}
                            </div>

                            <div className="studio-card__footer">
                                <button type="button" className="manga-primary-button" onClick={handleSavePageOrder} disabled={!hasSession || editSaving || editPages.length === 0}>
                                    {editSaving ? 'Сохраняем…' : 'Сохранить порядок'}
                                </button>
                                {editMessage ? <span className="manga-inline-note" role="status">{editMessage}</span> : null}
                            </div>
                            {canManageChapters ? (
                                <details className="studio-details studio-details--inline">
                                    <summary>Удаление главы</summary>
                                    <button className="manga-danger" onClick={handleDeleteChapter} disabled={!hasSession}>Удалить главу</button>
                                </details>
                            ) : null}
                        </div>
                    )}
                </div>
            </div>
        </PageShell>
    );
};

export default MangaStudioChaptersPage;
